import { createFileRoute, redirect } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import {
  Activity,
  ArrowDownToLine,
  Eye,
  FileEdit,
  FilePlus2,
  FolderPlus,
  Globe,
  LogIn,
  LogOut,
  RotateCw,
  Search,
  ScrollText,
  ShieldCheck,
  Trash2,
  UserPlus,
  X,
  ShieldAlert,
} from "lucide-react";
import { AppShell } from "@client/components/app-shell";
import { Input } from "@client/components/ui/input";
import { Button } from "@client/components/ui/button";
import { Badge } from "@client/components/ui/badge";
import { Spinner } from "@client/components/ui/spinner";
import { Tooltip, TooltipContent, TooltipTrigger } from "@client/components/ui/tooltip";
import { cn } from "@client/lib/cn";

interface AuditEvent {
  id: string;
  tenantId: string;
  actorId: string | null;
  actorName: string | null;
  actorIp: string | null;
  action: string;
  resourceType: string | null;
  resourceId: string | null;
  resourceName: string | null;
  occurredAt: string;
}

interface AuditResponse {
  items: AuditEvent[];
  nextCursor: string | null;
}

async function fetchAudit(cursor: string | null, action: string): Promise<AuditResponse> {
  const params = new URLSearchParams({ limit: "50" });
  if (cursor) params.set("cursor", cursor);
  if (action) params.set("action", action);
  const res = await fetch(`/api/audit?${params}`, { credentials: "include" });
  if (!res.ok) throw new Error("Erro ao consultar histórico");
  return res.json();
}

export const Route = createFileRoute("/admin/audit")({
  beforeLoad: async () => {
    const res = await fetch("/api/me", { credentials: "include" });
    if (!res.ok) throw redirect({ to: "/login" });
    const body = await res.json();
    if (body.user.role !== "admin") throw redirect({ to: "/dashboard" });
  },
  component: AuditViewerPage,
});

const QUICK_FILTERS: {
  label: string;
  value: string;
  icon: React.ComponentType<{ className?: string }>;
}[] = [
  { label: "Tudo", value: "", icon: Activity },
  { label: "Acessos", value: "login", icon: LogIn },
  { label: "Documentos", value: "document", icon: FileEdit },
  { label: "Pastas", value: "folder", icon: FolderPlus },
  { label: "Usuários", value: "user", icon: UserPlus },
];

interface ActionMeta {
  icon: React.ComponentType<{ className?: string }>;
  tone: "neutral" | "success" | "danger" | "warning" | "info" | "gold" | "outline";
  label: string;
}

function actionMeta(action: string): ActionMeta {
  const a = action.toLowerCase();
  if (a === "login") return { icon: LogIn, tone: "info", label: "Entrou no sistema" };
  if (a === "logout") return { icon: LogOut, tone: "neutral", label: "Saiu do sistema" };
  if (a === "login_failed")
    return { icon: ShieldAlert, tone: "danger", label: "Tentativa de acesso falhou" };
  if (a === "login.locked" || a === "user.locked")
    return { icon: ShieldAlert, tone: "danger", label: "Acesso bloqueado por segurança" };

  if (a.startsWith("user.")) {
    if (a.endsWith(".created"))
      return { icon: UserPlus, tone: "success", label: "Usuário cadastrado" };
    if (a.endsWith(".deleted")) return { icon: Trash2, tone: "danger", label: "Usuário removido" };
    if (a.includes("password"))
      return { icon: ShieldAlert, tone: "warning", label: "Senha alterada" };
    return { icon: UserPlus, tone: "gold", label: "Alteração em usuário" };
  }

  if (a.startsWith("document.")) {
    if (a.endsWith(".uploaded") || a.endsWith(".created"))
      return { icon: FilePlus2, tone: "success", label: "Documento enviado" };
    if (a.endsWith(".ready"))
      return { icon: FilePlus2, tone: "success", label: "Documento aprovado" };
    if (a.endsWith(".deleted"))
      return { icon: Trash2, tone: "danger", label: "Documento removido" };
    if (a.endsWith(".quarantined"))
      return { icon: ShieldAlert, tone: "danger", label: "Documento em quarentena" };
    if (a.endsWith(".rejected"))
      return { icon: ShieldAlert, tone: "danger", label: "Documento rejeitado" };
    if (a.endsWith(".restored"))
      return { icon: RotateCw, tone: "success", label: "Documento restaurado" };
    if (a.includes("download"))
      return { icon: ArrowDownToLine, tone: "neutral", label: "Documento baixado" };
    if (a.includes("opened") || a.includes("viewed"))
      return { icon: Eye, tone: "neutral", label: "Documento visualizado" };
    if (a.endsWith(".metadata_updated"))
      return { icon: FileEdit, tone: "warning", label: "Dados do documento alterados" };
    if (a.endsWith(".moved")) return { icon: FileEdit, tone: "warning", label: "Documento movido" };
    if (a.endsWith(".renamed"))
      return { icon: FileEdit, tone: "warning", label: "Documento renomeado" };
    return { icon: FileEdit, tone: "neutral", label: "Alteração em documento" };
  }

  if (a.startsWith("folder.")) {
    if (a.endsWith(".created")) return { icon: FolderPlus, tone: "success", label: "Pasta criada" };
    if (a.endsWith(".deleted")) return { icon: Trash2, tone: "danger", label: "Pasta removida" };
    if (a.endsWith(".renamed") || a.endsWith(".moved"))
      return { icon: FileEdit, tone: "warning", label: "Pasta alterada" };
    return { icon: FolderPlus, tone: "neutral", label: "Alteração em pasta" };
  }

  if (a.startsWith("ai.")) return { icon: Activity, tone: "info", label: "Ação de IA" };
  if (a.startsWith("audit.") || a === "partition.rotated")
    return { icon: RotateCw, tone: "outline", label: "Manutenção interna" };

  return { icon: Activity, tone: "neutral", label: action };
}

const RESOURCE_LABELS: Record<string, string> = {
  document: "Documento",
  folder: "Pasta",
  user: "Usuário",
  session: "Sessão",
};

function resourceLabel(type: string | null): string {
  if (!type) return "";
  return RESOURCE_LABELS[type.toLowerCase()] ?? type;
}

function formatDateTime(iso: string): { date: string; time: string; full: string } {
  const d = new Date(iso);
  return {
    date: d.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" }),
    time: d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }),
    full: d.toLocaleString("pt-BR"),
  };
}

function AuditViewerPage() {
  const [actionFilter, setActionFilter] = useState("");
  const [activeQuick, setActiveQuick] = useState("");
  const [cursor, setCursor] = useState<string | null>(null);
  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["audit", cursor, actionFilter],
    queryFn: () => fetchAudit(cursor, actionFilter),
  });

  function setQuick(value: string) {
    setActiveQuick(value);
    setActionFilter(value);
    setCursor(null);
  }

  const items = data?.items ?? [];

  return (
    <AppShell
      pageEyebrow="Histórico"
      pageTitle="Histórico de atividades"
      pageActions={
        <Badge variant="success">
          <ShieldCheck className="h-3 w-3" aria-hidden />
          Histórico íntegro
        </Badge>
      }
    >
      <div className="space-y-4">
        <div className="rounded-md hairline bg-[var(--color-surface)] overflow-hidden shadow-[var(--shadow-soft)]">
          <div className="flex flex-wrap items-center gap-1 px-3 py-2.5 hairline-b">
            {QUICK_FILTERS.map((f) => {
              const Icon = f.icon;
              const active = activeQuick === f.value;
              return (
                <button
                  key={f.value || "all"}
                  type="button"
                  onClick={() => setQuick(f.value)}
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded-sm px-3 py-1.5 text-sm font-medium transition-colors focus-ring",
                    active
                      ? "bg-[var(--color-ink)] text-[var(--color-surface)]"
                      : "text-[var(--color-ink-muted)] hover:bg-[var(--color-surface-3)] hover:text-[var(--color-ink)]",
                  )}
                >
                  <Icon className="h-3.5 w-3.5" aria-hidden />
                  {f.label}
                </button>
              );
            })}
          </div>
          <div className="relative px-3 py-2.5">
            <Search
              className="absolute left-6 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--color-ink-subtle)]"
              aria-hidden
            />
            <Input
              placeholder="Filtrar por tipo de atividade…"
              value={actionFilter}
              onChange={(e) => {
                const v = e.target.value;
                setActionFilter(v);
                setActiveQuick("");
                setCursor(null);
              }}
              aria-label="Filtrar atividades"
              className="h-10 pl-10 text-sm"
            />
            {actionFilter && (
              <button
                type="button"
                onClick={() => {
                  setActionFilter("");
                  setActiveQuick("");
                  setCursor(null);
                }}
                aria-label="Limpar filtro"
                className="absolute right-5 top-1/2 -translate-y-1/2 rounded-sm p-1.5 text-[var(--color-ink-muted)] hover:text-[var(--color-ink)] hover:bg-[var(--color-surface-3)] focus-ring"
              >
                <X className="h-3.5 w-3.5" aria-hidden />
              </button>
            )}
          </div>
        </div>

        <div className="rounded-md hairline bg-[var(--color-surface)] overflow-hidden shadow-[var(--shadow-soft)]">
          <div className="overflow-x-auto">
            <div className="min-w-[760px]">
              <div className="grid grid-cols-[110px_minmax(0,1.6fr)_minmax(0,1fr)_120px_minmax(0,1.2fr)] gap-3 px-4 py-3 text-label text-[var(--color-ink-muted)] hairline-b bg-[var(--color-canvas)]">
                <span>Quando</span>
                <span>Atividade</span>
                <span>Quem</span>
                <span>Origem</span>
                <span>Item</span>
              </div>

              {isLoading ? (
                <div className="flex items-center gap-3 px-4 py-10 text-sm text-[var(--color-ink-muted)]">
                  <Spinner size="sm" />
                  Carregando atividades…
                </div>
              ) : items.length === 0 ? (
                <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
                  <div className="flex h-14 w-14 items-center justify-center rounded-md hairline bg-[var(--color-canvas)] text-[var(--color-ink-subtle)] mb-4">
                    <ScrollText className="h-6 w-6" aria-hidden />
                  </div>
                  <h3 className="text-title text-base text-[var(--color-ink)]">
                    Nenhuma atividade encontrada
                  </h3>
                  <p className="text-sm text-[var(--color-ink-muted)] mt-2 max-w-xs">
                    {actionFilter
                      ? "Nenhum registro corresponde ao filtro aplicado. Tente outro termo."
                      : "Ainda não há atividades registradas no sistema."}
                  </p>
                </div>
              ) : (
                <ul>
                  {items.map((e) => {
                    const meta = actionMeta(e.action);
                    const Icon = meta.icon;
                    const time = formatDateTime(e.occurredAt);
                    return (
                      <li
                        key={e.id}
                        className="grid grid-cols-[110px_minmax(0,1.6fr)_minmax(0,1fr)_120px_minmax(0,1.2fr)] items-center gap-3 px-4 py-3 hairline-b last:border-b-0 transition-colors hover:bg-[var(--color-surface-2)]"
                      >
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="text-sm leading-tight cursor-default">
                              <div className="text-[var(--color-ink-2)]">{time.date}</div>
                              <div className="text-xs text-[var(--color-ink-muted)] mt-0.5">
                                {time.time}
                              </div>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>{time.full}</TooltipContent>
                        </Tooltip>

                        <div className="flex items-center gap-2.5 min-w-0">
                          <span
                            aria-hidden
                            className={cn(
                              "shrink-0 flex h-7 w-7 items-center justify-center rounded-sm",
                              meta.tone === "success" &&
                                "bg-[var(--color-success-wash)] text-[var(--color-success)]",
                              meta.tone === "danger" &&
                                "bg-[var(--color-danger-wash)] text-[var(--color-danger)]",
                              meta.tone === "warning" &&
                                "bg-[var(--color-warning-wash)] text-[var(--color-warning)]",
                              meta.tone === "info" &&
                                "bg-[var(--color-info-wash)] text-[var(--color-info)]",
                              meta.tone === "gold" &&
                                "bg-[var(--color-gold-wash)] text-[var(--color-gold)]",
                              (meta.tone === "neutral" || meta.tone === "outline") &&
                                "bg-[var(--color-surface-3)] text-[var(--color-ink-muted)]",
                            )}
                          >
                            <Icon className="h-3.5 w-3.5" />
                          </span>
                          <span className="text-sm text-[var(--color-ink)] truncate">
                            {meta.label}
                          </span>
                        </div>

                        {e.actorName ? (
                          <span
                            className="text-sm text-[var(--color-ink)] truncate"
                            title={e.actorName}
                          >
                            {e.actorName}
                          </span>
                        ) : e.actorId ? (
                          <span className="text-sm text-[var(--color-ink-muted)] italic truncate">
                            Usuário removido
                          </span>
                        ) : (
                          <span className="text-sm text-[var(--color-ink-faint)]">Sistema</span>
                        )}

                        <div className="flex items-center gap-1.5">
                          {e.actorIp ? (
                            <>
                              <Globe
                                className="h-3.5 w-3.5 text-[var(--color-ink-muted)] shrink-0"
                                aria-hidden
                              />
                              <span className="text-mono text-xs text-[var(--color-ink-muted)] truncate">
                                {e.actorIp}
                              </span>
                            </>
                          ) : (
                            <span className="text-sm text-[var(--color-ink-faint)]">—</span>
                          )}
                        </div>

                        {e.resourceType && e.resourceId ? (
                          <div className="flex items-baseline gap-1.5 min-w-0">
                            <span className="shrink-0 text-xs uppercase tracking-wider text-[var(--color-ink-muted)] font-semibold">
                              {resourceLabel(e.resourceType)}
                            </span>
                            {e.resourceName ? (
                              <span
                                className="text-sm text-[var(--color-ink)] truncate"
                                title={e.resourceName}
                              >
                                {e.resourceName}
                              </span>
                            ) : (
                              <span className="text-sm text-[var(--color-ink-muted)] italic truncate">
                                removido
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-sm text-[var(--color-ink-faint)]">—</span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}

              {data?.nextCursor && (
                <div className="hairline-t px-4 py-3 flex items-center justify-between bg-[var(--color-canvas)]">
                  <span className="text-sm text-[var(--color-ink-muted)]">
                    {items.length} atividade{items.length === 1 ? "" : "s"} carregada
                    {items.length === 1 ? "" : "s"}
                  </span>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setCursor(data.nextCursor)}
                    disabled={isFetching}
                  >
                    {isFetching ? <Spinner size="sm" /> : "Carregar mais"}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="rounded-md hairline bg-[var(--color-surface-2)] p-4 text-sm text-[var(--color-ink-muted)] leading-relaxed flex items-start gap-3">
          <ShieldCheck
            className="h-4 w-4 mt-0.5 text-[var(--color-success)] shrink-0"
            aria-hidden
          />
          <p>
            Os registros deste histórico{" "}
            <strong className="text-[var(--color-ink)]">
              não podem ser apagados nem alterados
            </strong>
            . Cada entrada é assinada digitalmente, garantindo que o histórico original do
            escritório permanece intacto.
          </p>
        </div>
      </div>
    </AppShell>
  );
}
